import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Bloque de fecha (contrato §2.4/§5): compara con `start`, AND con texto/filtros, sin límite de
// fechas futuras ni pasadas (pueden buscar contenidos ya programados). `from`/`to` viajan siempre
// en formato YYYY-MM-DD, igual que exige la API.
//
// A petición del equipo, sin conmutador "Fecha concreta" / "Rango": siempre se muestran los dos
// campos Desde/Hasta. Al rellenar uno con el otro vacío, se abre el calendario del otro campo SIN
// copiarle la fecha: así, elija el día que elija, su valor pasa de vacío a una fecha real y
// dispara "onChange" de forma garantizada. Esta primera elección se queda en un borrador local
// (`draft`) y no dispara la búsqueda (el `onChange` del prop) hasta que el segundo campo se
// completa (elige una fecha, o su calendario se cierra sin elegir ninguna — ver
// `handleCalendarClose`). Con las dos fechas ya puestas, cambiar una se sigue aplicando al
// instante, sin botón. Solo "Hasta" restringe su calendario con `minDate` (Desde ≤ Hasta); "Desde"
// no tiene `maxDate`, así que si el user lo pone por delante de la "Hasta" ya puesta, es esta
// última la que se invalida (ver `handleChange`).
//
// Calendario propio (react-datepicker, ya dependencia del proyecto — se usa igual en
// web/user/.../Register) en vez de `<input type="date">` nativo: el calendario nativo de Chrome
// selecciona una fecha y se cierra solo con navegar de mes (con las flechas del propio calendario,
// o subiendo/bajando mes), sin que el user llegue a pinchar ningún día — comportamiento del propio
// control nativo, no arreglable por JS sobre un `<input type="date">` (no hay forma de distinguir
// "cambió de mes" de "eligió un día" con ese control). react-datepicker sí lo permite: solo llama a
// `onChange` cuando se pincha (o se teclea) una fecha real; navegar de mes con sus flechas o su
// selector de mes/año no lo dispara nunca.
const isoToDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toISO = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDisplay = (iso) => {
  const d = isoToDate(iso);
  return d ? d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
};

const CalendarIcon = ({ onClick }) => (
  <svg className="bq-icon bq-date-icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" onClick={onClick}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="3" x2="8" y2="7" />
    <line x1="16" y1="3" x2="16" y2="7" />
  </svg>
);

const DateFilter = ({ from, to, onChange, mobile }) => {
  const { t } = useTranslation();
  // Borrador local (petición del equipo, ítem 1): ver comentario de cabecera.
  const [draft, setDraft] = useState({ from: from || "", to: to || "" });
  const [pendingField, setPendingField] = useState(null);
  // Campo cuyo calendario está abierto ahora mismo (ítem 2, petición del equipo): gatea la clase
  // "selected" que marca visualmente el campo activo. Se sincroniza con el estado real del
  // calendario de react-datepicker vía onCalendarOpen/onCalendarClose, así que refleja lo mismo
  // tanto si lo abre el user (click/foco) como si se abre por programa (`openPicker`).
  const [focusedField, setFocusedField] = useState(null);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  // Sincroniza cambios externos (chip "x", "Limpiar filtros", "Borrar filtros" del modal,
  // atrás/adelante del navegador) salvo mientras haya una selección a medias sin confirmar, para
  // no perderla.
  useEffect(() => {
    if (pendingField) return;
    setDraft({ from: from || "", to: to || "" });
  }, [from, to, pendingField]);

  const hasValue = !!draft.from || !!draft.to;

  // Abre/cierra el calendario de `field` por programa (equivalente a que el user lo hiciera):
  // `setOpen` es la API imperativa de react-datepicker, expuesta en la instancia vía `ref`.
  const openPicker = (field) => {
    const ref = field === "from" ? fromRef : toRef;
    ref.current?.setOpen(true);
  };
  const closePicker = (field) => {
    const ref = field === "from" ? fromRef : toRef;
    ref.current?.setOpen(false);
  };
  const toggleField = (field) => () => {
    if (focusedField === field) closePicker(field);
    else openPicker(field);
  };

  // Único sitio donde se resuelve un `pendingField`: si el user SÍ elige una fecha, `handleChange`
  // ya lo limpia y dispara la búsqueda por su cuenta (este guard hace que, si además se dispara un
  // cierre de calendario justo después —selección con `shouldCloseOnSelect`—, no se repita nada).
  // Si en cambio el calendario se cierra SIN elegir fecha (Escape, click fuera, o volver a pinchar
  // el icono para cerrarlo), se confirma la búsqueda con el borrador tal cual estaba.
  const handleCalendarClose = (field) => () => {
    setFocusedField((current) => (current === field ? null : current));
    if (pendingField === field) {
      setPendingField(null);
      onChange(draft.from, draft.to);
    }
  };

  const handleChange = (field) => (date) => {
    const val = toISO(date);
    const other = field === "from" ? "to" : "from";
    const wasEmpty = !draft.from && !draft.to;
    const next = { ...draft, [field]: val };

    // "Desde" no tiene `maxDate` (el user puede elegir libremente cualquier día). Si el resultado
    // invalida el rango (Desde > Hasta), la "Hasta" antigua ya no vale: se sustituye por el mismo
    // día que "Desde" (para que su calendario aparezca ya marcado ahí al abrirlo) y queda
    // pendiente de confirmar, igual que el primer relleno.
    if (field === "from" && next.to && next.from > next.to) {
      next.to = next.from;
      setDraft(next);
      setPendingField("to");
      openPicker("to");
      return;
    }

    setDraft(next);
    if (wasEmpty) {
      setPendingField(other);
      openPicker(other);
      return;
    }
    setPendingField(null);
    onChange(next.from, next.to);
  };

  const summary =
    draft.from && draft.to && draft.from !== draft.to
      ? `${formatDisplay(draft.from)} – ${formatDisplay(draft.to)}`
      : formatDisplay(draft.from || draft.to);

  return (
    <div className={`bq-date${hasValue ? " has-value" : ""}`}>
      {!mobile && (
        <div className="bq-date-label">
          <span>{t("search.buscar.date")}</span>
        </div>
      )}
      <div className="bq-date-fields">
        <div className="bq-date-field">
          <label htmlFor="bq-date-from" className="bq-visually-hidden">
            {t("search.buscar.dateFrom")}
          </label>
          <DatePicker
            id="bq-date-from"
            ref={fromRef}
            selected={isoToDate(draft.from)}
            onChange={handleChange("from")}
            onCalendarOpen={() => setFocusedField("from")}
            onCalendarClose={handleCalendarClose("from")}
            dateFormat="dd/MM/yy"
            locale="es"
            placeholderText="--/--/--"
            autoComplete="off"
            className={focusedField === "from" ? "selected" : undefined}
            popperClassName="bq-date-popper"
            popperPlacement="bottom-start"
          />
          <CalendarIcon onClick={toggleField("from")} />
        </div>
        <span className="bq-date-sep">–</span>
        <div className="bq-date-field">
          <label htmlFor="bq-date-to" className="bq-visually-hidden">
            {t("search.buscar.dateTo")}
          </label>
          <DatePicker
            id="bq-date-to"
            ref={toRef}
            selected={isoToDate(draft.to)}
            onChange={handleChange("to")}
            onCalendarOpen={() => setFocusedField("to")}
            onCalendarClose={handleCalendarClose("to")}
            minDate={isoToDate(draft.from)}
            dateFormat="dd/MM/yy"
            locale="es"
            placeholderText="--/--/--"
            autoComplete="off"
            className={focusedField === "to" ? "selected" : undefined}
            popperClassName="bq-date-popper"
            popperPlacement="bottom-start"
          />
          <CalendarIcon onClick={toggleField("to")} />
        </div>
      </div>
      {hasValue && <span className="bq-visually-hidden">{summary}</span>}
    </div>
  );
};

export default DateFilter;
export { formatDisplay as formatDateDisplay };
